import { useState, useEffect, useCallback } from 'react'
import { BreakdownItem, OfferCurrency } from '../components/offer/FeeBreakdown'

export interface SavedTemplate {
  id: string
  name: string
  modality: string
  breakdown: Omit<BreakdownItem, 'currency'>[]
  createdAt: string
  updatedAt: string
}

const STORAGE_KEY = 'offer_templates'

/**
 * Hook para gerenciar templates de ofertas salvos
 * Por enquanto usa localStorage, futuramente será migrado para API
 */
export function useSavedTemplates(modality?: string) {
  const [templates, setTemplates] = useState<SavedTemplate[]>([])
  const [loading, setLoading] = useState(true)

  // Load templates on mount
  useEffect(() => {
    loadTemplates()
  }, [])

  const loadTemplates = useCallback(() => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const all = JSON.parse(stored) as SavedTemplate[]
        // Filter by modality if provided
        const filtered = modality
          ? all.filter(t => t.modality === modality || t.modality === 'all')
          : all
        setTemplates(filtered)
      }
    } catch (error) {
      console.error('Error loading templates:', error)
    } finally {
      setLoading(false)
    }
  }, [modality])

  // Save a new template
  const saveTemplate = useCallback((
    name: string,
    breakdown: BreakdownItem[],
    currentModality: string
  ): SavedTemplate => {
    const newTemplate: SavedTemplate = {
      id: `template-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name,
      modality: currentModality,
      breakdown: breakdown.map(({ currency, ...rest }) => rest),
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    }

    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      const all = stored ? JSON.parse(stored) as SavedTemplate[] : []
      all.push(newTemplate)
      localStorage.setItem(STORAGE_KEY, JSON.stringify(all))
      loadTemplates()
    } catch (error) {
      console.error('Error saving template:', error)
    }

    return newTemplate
  }, [loadTemplates])

  // Delete a template
  const deleteTemplate = useCallback((id: string) => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const all = JSON.parse(stored) as SavedTemplate[]
        const filtered = all.filter(t => t.id !== id)
        localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered))
        loadTemplates()
      }
    } catch (error) {
      console.error('Error deleting template:', error)
    }
  }, [loadTemplates])

  // Apply a template to current breakdown
  const applyTemplate = useCallback((
    template: SavedTemplate,
    currency: OfferCurrency
  ): BreakdownItem[] => {
    return template.breakdown.map(item => ({
      ...item,
      currency,
    }))
  }, [])

  // Update template
  const updateTemplate = useCallback((
    id: string,
    name: string,
    breakdown: BreakdownItem[]
  ) => {
    try {
      const stored = localStorage.getItem(STORAGE_KEY)
      if (stored) {
        const all = JSON.parse(stored) as SavedTemplate[]
        const index = all.findIndex(t => t.id === id)
        if (index !== -1) {
          all[index] = {
            ...all[index],
            name,
            breakdown: breakdown.map(({ currency, ...rest }) => rest),
            updatedAt: new Date().toISOString(),
          }
          localStorage.setItem(STORAGE_KEY, JSON.stringify(all))
          loadTemplates()
        }
      }
    } catch (error) {
      console.error('Error updating template:', error)
    }
  }, [loadTemplates])

  return {
    templates,
    loading,
    saveTemplate,
    deleteTemplate,
    applyTemplate,
    updateTemplate,
    refresh: loadTemplates,
  }
}
